import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import { SecurityCard } from '@/common/ui/SecurityCard'
import { H3 } from '@/common/ui'
import { TradingBlankState } from '@/common/assets/svg/tradingBlankState'
import { Link } from 'react-router-dom'
import { APP_ROUTES } from '@/common/constants.ts'
import { TradingPageSecurityCardClickedAnalytics } from '@/common/analytics/events/TradingPageSecurityCardClickedAnalytics'
import { FIND_STOCKS_COPY } from '@/locales/en'
import { useOrderSource } from '@/common/context/useOrderSource'
import { ORDER_ENTRY_POINT, ORDER_FROM } from '@/common/context/orderSourceValues'

type Props = {
    filteredSecurities: any[] | undefined
    isLoading: boolean
    dataSource: string
    stocksKey: string
    positiveFilterCount: number
}

export function SecuritiesGrid({ filteredSecurities, isLoading, dataSource, stocksKey, positiveFilterCount }: Props) {
    const { setOrderSource } = useOrderSource()
    if (isLoading && dataSource === stocksKey) return <FullScreenLoader />

    if (filteredSecurities?.length) {
        return (
            <div className="px-3 md:px-6">
                <div className="text-black text-sm font-semibold font-['Inter'] leading-tight pt-6">
                    {FIND_STOCKS_COPY.results.replace('{count}', filteredSecurities?.length.toString() ?? '0')}
                </div>
                <div className="grid mt-5 grid-wrap grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                    {filteredSecurities.map((security: any) => {
                        const price =
                            security.securityDetails.priceInfo.buyPrice ??
                            security.securityDetails.priceInfo.closingPrice
                        return (
                            <Link
                                key={security.securityDetails.securityCode}
                                className="w-full text-current hover:underline"
                                to={`${APP_ROUTES.SECURITY}/${security.securityDetails.securityCode}`}
                                onClick={() => {
                                    setOrderSource(ORDER_FROM.FIND_STOCKS, ORDER_ENTRY_POINT.FILTERS)
                                    TradingPageSecurityCardClickedAnalytics.track({
                                        securityCode: security.securityDetails.securityCode,
                                        securityName:
                                            security.securityDetails.companyName ??
                                            security.securityName ??
                                            security.securityDetails.securityName,
                                        price: price,
                                        changeInPriceYTD: security.changeInPriceYTD,
                                        revenueSource: security.ethicalTags.map((et: any) => et.tag),
                                        numPositiveTags: security.ethicalTags.reduce(
                                            (acc: number, curr: any) => acc + (curr.isTagPositive ? 1 : 0),
                                            0,
                                        ),
                                        numNegativeTags: security.ethicalTags.reduce(
                                            (acc: number, curr: any) => acc + (curr.isTagPositive ? 0 : 1),
                                            0,
                                        ),
                                    })
                                }}
                            >
                                <SecurityCard
                                    loading={isLoading}
                                    securityCode={security.securityDetails.securityCode}
                                    securityName={
                                        security.securityDetails.companyName ??
                                        security.securityName ??
                                        security.securityDetails.securityName
                                    }
                                    price={price}
                                    changeInPriceYTD={security.changeInPriceYTD}
                                    historicalPrices={security.historicalPrices}
                                    ethicalTags={security.ethicalTags}
                                    withEthicalTags
                                />
                            </Link>
                        )
                    })}
                </div>
            </div>
        )
    }

    if (!positiveFilterCount) {
        return (
            <div className="text-foreground text-center flex flex-col gap-12 py-12 items-center">
                <TradingBlankState />
                <div className="max-w-sm flex flex-col gap-2">
                    <H3>{FIND_STOCKS_COPY.noPositiveFilters.title}</H3>
                    <p>{FIND_STOCKS_COPY.noPositiveFilters.description}</p>
                </div>
            </div>
        )
    }

    return (
        <div className="text-foreground text-center flex flex-col gap-2 py-6">
            <H3>{FIND_STOCKS_COPY.noResults.title}</H3>
            <p>{FIND_STOCKS_COPY.noResults.description}</p>
        </div>
    )
}

export default SecuritiesGrid
